import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Anchor, Stethoscope, UserRound, Scissors, ShieldCheck, ArrowRight } from "lucide-react";
import InstallPWAButton from "@/components/InstallPWAButton";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth(role) {
  const redirectUrl = window.location.origin + `/?signup_role=${role}`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const BG = "https://static.prod-images.emergentagent.com/jobs/18558f8e-d20a-418c-ad14-9bf948245a62/images/16e4f44db9ebf94598c6f50c9d2d5bcbc1ce76ce104025c8ad10693752a47ee8.png";

const ROLES = [
  { value: "patient", label: "Patient", icon: UserRound, tagline: "Complete your pre-op intake online", canRegister: true, canGoogle: true },
  { value: "surgeon", label: "Surgeon", icon: Scissors, tagline: "Request an anesthetist for your procedures", canRegister: true, canGoogle: true },
  { value: "anesthetist", label: "Anesthetist", icon: Stethoscope, tagline: "Access your duty schedule & coverage", canRegister: false, canGoogle: false },
  { value: "admin", label: "Admin", icon: ShieldCheck, tagline: "Manage operations & users", canRegister: false, canGoogle: false },
];

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [activeRole, setActiveRole] = useState("patient");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email.trim(), password);
    setBusy(false);
    if (!r.ok) {
      toast.error(r.error);
      return;
    }
    if (r.user.role !== activeRole) {
      toast.warning(`Signed in as ${r.user.role}. Redirecting…`);
    } else {
      toast.success("Welcome back");
    }
    nav("/");
  };

  const role = ROLES.find((r) => r.value === activeRole);

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden" style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E6B6B]/85 via-[#339999]/70 to-[#1E6B6B]/85" />
        <div className="relative z-10 text-white">
          <div className="flex items-center gap-3">
            <img src="/brand/aamg-logo.png" alt="AAMG — Anesthesia Associates Medical Group" className="h-14 md:h-16 w-auto aamg-logo-glow" />
          </div>
        </div>
        <div className="relative z-10 text-white space-y-4 max-w-md">
          <p className="aamg-label text-white/80">Perioperative Surgical Home</p>
          <h1 className="text-3xl xl:text-4xl font-semibold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Compassionate Care. <span className="text-[#E0A96D]">Clinical Excellence.</span>
          </h1>
          <p className="text-white/85 text-sm leading-relaxed">
            The pre-op intake, scheduling, and follow-up portal of Anesthesia Associates Medical Group — physician-led anesthesia care across The Bahamas.
          </p>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {["Transparency", "Integrity", "Honesty", "Teamwork", "Accountability"].map((v) => (
              <span key={v} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/15 border border-white/25">{v}</span>
            ))}
          </div>
          <div className="pt-3">
            <InstallPWAButton variant="default" label="Download the AAMG app" className="shadow-lg shadow-black/20" data-testid="install-pwa-hero" />
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 relative">
        <div className="w-full max-w-md mx-auto" data-testid="login-card">
          <div className="lg:hidden mb-6 flex items-center justify-between gap-3">
            <div className="inline-flex bg-[#0a1628] rounded-md px-3 py-2">
              <img src="/brand/aamg-logo.png" alt="AAMG" className="h-9 w-auto aamg-logo-glow" />
            </div>
            <InstallPWAButton variant="outline" label="Download app" size="sm" />
          </div>
          <p className="aamg-label mb-2">Sign in</p>
          <h2 className="text-3xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Welcome back</h2>
          <p className="text-slate-500 text-sm mb-6">Choose your portal and sign in.</p>

          <Tabs value={activeRole} onValueChange={setActiveRole} className="mb-5">
            <TabsList className="grid grid-cols-4 w-full bg-slate-100" data-testid="role-tabs">
              {ROLES.map((r) => (
                <TabsTrigger key={r.value} value={r.value} data-testid={`role-tab-${r.value}`} className="data-[state=active]:bg-white data-[state=active]:text-[#339999] data-[state=active]:shadow-sm">
                  <r.icon className="h-3.5 w-3.5 mr-1" /> {r.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {ROLES.map((r) => (
              <TabsContent key={r.value} value={r.value} className="mt-3">
                <p className="text-xs text-slate-500">{r.tagline}</p>
              </TabsContent>
            ))}
          </Tabs>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="field-label">Email</Label>
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="field-label">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full bg-[#339999] hover:bg-[#2A7F7F] h-11">
              {busy ? "Signing in…" : `Sign in as ${role.label}`}
            </Button>
          </form>

          {role.canGoogle && (
            <>
              <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
                <div className="flex-1 h-px bg-slate-200" /> OR <div className="flex-1 h-px bg-slate-200" />
              </div>
              <Button onClick={() => startGoogleAuth(activeRole)} variant="outline" className="w-full h-11 border-slate-300" data-testid="login-google-btn">
                <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3C29.3 35 26.8 36 24 36c-5.3 0-9.8-3.4-11.3-8L6 28c3.3 6.6 10 10 18 10z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.6 5l6.3 5.3C42 35 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
                Continue with Google
              </Button>
            </>
          )}

          <div className="text-center text-sm text-slate-500 mt-7">
            {role.canRegister ? (
              <>New to AAMG?{" "}
                <Link to={`/register?role=${activeRole}`} className="text-[#339999] font-medium hover:underline" data-testid={`register-link-${activeRole}`}>
                  Register as {role.label.toLowerCase()} <ArrowRight className="inline h-3 w-3" />
                </Link>
              </>
            ) : (
              <span className="text-slate-400 italic">{role.label}s are onboarded by AAMG administrators.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
